/**
 * User role hierarchy. Mirrors documents/architecture/roles.md (the source of truth).
 *
 *   SYSTEM_ADMIN  (AMLiQ super admin — internal team, cross-account)
 *        │
 *   ┌────┴──────────────── Account (the business tenant) ──────────────┐
 *   │  ADMIN_USER         (top admin within an account)                 │
 *   │       │                                                           │
 *   │  COMPLIANCE_OFFICER (like Admin, minus account profile/settings)  │
 *   │       │                                                           │
 *   │  STANDARD_USER      (submits & views only their own checks)       │
 *   └───────────────────────────────────────────────────────────────── ┘
 *
 * `accountId` (tenant scope) lives on the user object, not here — these
 * are the role *kinds*. Account/Subscriber is an entity, not a role.
 */

export const UserRole = {
  /** AMLiQ super admin — internal team, cross-account. Refined later. */
  SystemAdmin: 'SYSTEM_ADMIN',
  /** Admin user — highest admin inside a customer account. */
  AdminUser: 'ADMIN_USER',
  /** Compliance officer — near-identical to Admin, minus account settings. */
  ComplianceOfficer: 'COMPLIANCE_OFFICER',
  /** Standard user — submits and sees only their own checks. */
  StandardUser: 'STANDARD_USER',
} as const

export type UserRole = (typeof UserRole)[keyof typeof UserRole]

export const ALL_ROLES: ReadonlyArray<UserRole> = Object.values(UserRole)

/**
 * Numeric authority level. Higher = more authority. Used for
 * "at least this role" checks (`hasMinRole`).
 */
export const ROLE_LEVEL: Record<UserRole, number> = {
  [UserRole.SystemAdmin]: 100,
  [UserRole.AdminUser]: 50,
  [UserRole.ComplianceOfficer]: 30,
  [UserRole.StandardUser]: 10,
}

/** Human-readable labels for UI (tags, dropdowns). */
export const ROLE_LABELS: Record<UserRole, string> = {
  [UserRole.SystemAdmin]: 'Super Admin',
  [UserRole.AdminUser]: 'Admin User',
  [UserRole.ComplianceOfficer]: 'Compliance Officer',
  [UserRole.StandardUser]: 'Standard User',
}

/** True for platform-level (non-tenant-scoped) roles. */
export function isPlatformRole(role: UserRole): boolean {
  return role === UserRole.SystemAdmin
}
